<script setup lang="ts">
import {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogRoot,
  AlertDialogTitle,
} from 'reka-ui'
import { ref, watch } from 'vue'

interface Props {
  show: boolean
  title: string
  message?: string
  initialValue?: string
  placeholder?: string
  confirmText?: string
  cancelText?: string
  metadata?: any // 任意元数据，随 confirm 事件一起返回
}

const props = withDefaults(defineProps<Props>(), {
  message: '',
  initialValue: '',
  placeholder: '',
  confirmText: '确认',
  cancelText: '取消',
  metadata: undefined,
})

const emit = defineEmits<{
  (e: 'confirm', value: string, metadata?: any): void
  (e: 'cancel'): void
}>()

const inputValue = ref('')
const isConfirming = ref(false) // 防止 confirm 和 cancel 同时触发

watch(() => props.show, (show) => {
  if (show) {
    inputValue.value = props.initialValue
    isConfirming.value = false // 重置状态
  }
})

function handleConfirm() {
  isConfirming.value = true // 标记正在确认
  emit('confirm', inputValue.value, props.metadata)
}

function handleCancel() {
  if (isConfirming.value) {
    return
  }
  emit('cancel')
}
</script>

<template>
  <AlertDialogRoot :open="show" @update:open="(val) => !val && handleCancel()">
    <AlertDialogPortal>
      <AlertDialogOverlay
        class="fixed inset-0 z-100 bg-black/50 backdrop-blur-sm data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn"
      />
      <AlertDialogContent
        class="fixed left-1/2 top-1/2 z-100 max-w-md w-[90%] border border-neutral-200 rounded-xl bg-white p-6 shadow-xl -translate-x-1/2 -translate-y-1/2 data-[state=closed]:animate-contentHide data-[state=open]:animate-contentShow dark:border-neutral-700 dark:bg-neutral-800"
      >
        <AlertDialogTitle class="mb-4 text-lg text-neutral-800 dark:text-neutral-100">
          {{ title }}
        </AlertDialogTitle>
        <!-- 始终渲染 Description 以避免无障碍警告，message 为空时隐藏 -->
        <AlertDialogDescription :class="message ? 'mb-4 text-sm text-neutral-600 dark:text-neutral-300' : 'sr-only'">
          {{ message || ' ' }}
        </AlertDialogDescription>

        <!-- 输入框 -->
        <textarea
          v-model="inputValue"
          :placeholder="placeholder"
          class="mb-6 w-full resize-none border border-neutral-300 rounded-lg bg-white px-3 py-2 text-neutral-800 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows="4"
          @keydown.enter.meta="handleConfirm"
          @keydown.enter.ctrl="handleConfirm"
        />

        <div class="flex flex-row justify-end gap-3">
          <AlertDialogCancel as-child>
            <button
              class="rounded-lg bg-neutral-100 px-4 py-2 text-neutral-700 transition-colors dark:bg-neutral-700 hover:bg-neutral-200 dark:text-neutral-200 dark:hover:bg-neutral-600"
              @click="handleCancel"
            >
              {{ cancelText }}
            </button>
          </AlertDialogCancel>
          <AlertDialogAction as-child>
            <button
              class="rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
              @click="handleConfirm"
            >
              {{ confirmText }}
            </button>
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>
